import "@awesome.me/webawesome/dist/components/button/button.js";
import { useGSAP } from "@gsap/react";
import Image from "next/image";
import { gsap } from "gsap";
import { SplitText } from "gsap/SplitText";
import { ButtonLink } from "../button-link";
import { setNavDark, setNavLight } from "@/app/utils/nav";
import { Breadcrumb } from "../breadcrumb";
import { Heading1 } from "../typography";
import { BannerBlob, BannerStats, type BannerStat } from "../shared";

const bannerStats: BannerStat[] = [
  { value: "10,000+", label: "Irrigation Properties", color: "blue" },
  { value: "3,600", label: "Site Visits in 2023/24", color: "green" },
  { value: "368", label: "Enforcement Actions", color: "orange" },
];

export const Banner = () => {
  const breadCrumbItems = [
    { label: "Home", href: "/" },
    { label: "Case Studies", href: "/case-studies" },
    { label: "NSW Water Compliance" },
  ];

  useGSAP(() => {
    const splitWords = new SplitText(".banner-text", {
      type: "words, lines",
      mask: "lines",
    });

    const tl = gsap.timeline({
      ease: "none",
    });

    tl.from(splitWords.words, {
      y: 100,
      stagger: 0.1,
      autoAlpha: 0,
    });

    const maskImageTl = gsap.timeline({
      paused: true,
      ease: "none",
      scrollTrigger: {
        trigger: ".nsw-compliance-banner__wrapper",
        start: "top top",
        end: "bottom top",
        pin: ".nsw-compliance-banner",
        invalidateOnRefresh: true,
        scrub: true,
        // markers: true,
        onUpdate: (self) => {
          if (self.progress > 0.47) {
            setNavDark();
          } else {
            setNavLight();
          }
        },
      },
    });

    maskImageTl
      .to("#clipped-section", {
        opacity: 1,
        duration: 0,
      })
      .to("#masked-image__img-full", {
        scale: 1,
      })
      .to(
        "#clipped-section",
        {
          clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
          immediateRender: false,
        },
        "<"
      )
      .to(
        "#clipped-section__text",
        {
          scale: 1,
          opacity: 1,
        },
        "<0.9"
      )
      .to(
        "#clipped-section__progress-bar",
        {
          clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
        },
        "<"
      );
  }, []);

  return (
    <section className="nsw-compliance-banner__wrapper">
      <div className="nsw-compliance-banner relative pt-28 lg:pt-32 bg-cover bg-center w-full h-dvh flex flex-col justify-center items-center text-white text-center overflow-hidden">
        <div className="container relative z-20">
          <Breadcrumb items={breadCrumbItems} className="mb-8" />
        </div>
        <div className="container relative flex-1 z-20 flex flex-col justify-center">
          <div className="pb-56">
            <Heading1 className="banner-text">
              Water license compliance monitoring in NSW
            </Heading1>

            {/* Stats Grid */}
            <BannerStats
              stats={bannerStats}
              className="mt-8 lg:mt-12 max-w-4xl justify-center mx-auto"
            />

            <div
              id="banner-buttons"
              className="flex items-center justify-center gap-4 md:gap-8 mt-8 lg:mt-12 lg:gap-10 flex-wrap"
            >
              <ButtonLink
                backgroundColorClass="bg-haizea-blue"
                href="https://demo.extractortool.au/"
                target="_blank"
              >
                Try the Extractor
              </ButtonLink>
            </div>
          </div>
        </div>
        <div className="absolute inset-0 bg-cover bg-center z-10 opacity-50">
          <Image
            src="/images/nsw-water-compliance.webp"
            alt="" // Decorative background
            fill
            className="object-cover"
            priority
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-haizea-green/50 via-haizea-dark/50 via-70% to-transparent" />
        </div>
        <BannerBlob className="opacity-50" />
        <Image
          id="scroll-down-indicator"
          src="/images/scroll-indicator.svg"
          alt="" // Decorative scroll indicator
          width={24}
          height={24}
          aria-hidden="true"
          className="absolute bottom-4 left-[50%] transform -translate-x-[50%] w-8 h-8 z-20 filter invert animate-bounce text-pink-800"
        />
        <div
          id="clipped-section"
          className="absolute inset-0 h-screen z-50 opacity-0 [clip-path:polygon(10%_75%,90%_75%,90%_90%,10%_90%)] md:[clip-path:polygon(40%_80%,60%_80%,60%_90%,40%_90%)] xl:[clip-path:polygon(42%_80%,58%_80%,58%_90%,42%_90%)]"
        >
          <Image
            id="masked-image__img-full"
            src="/images/river-basin.webp"
            height={180}
            width={530}
            alt="" // Decorative masked image
            className="size-full object-cover scale:0 md:scale-45 origin-bottom"
            draggable={false}
            unoptimized={true}
            style={{ transformOrigin: "50% 98%" }}
          />

          <div
            id="clipped-section__text"
            className="absolute inset-0 w-[712px] h-max md:h-[315px] bg-[#0D2949]/85 
              border border-gray-500 px-10 py-8 lg:px-18 lg:py-16 rounded-2xl 
              z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
              sizing-border opacity-0 scale-75 overflow-hidden max-w-[90vw] max-h-[90vh]"
          >
            <p className="text-xl md:text-2xl m-0 font-light">
              Haizea’s satellite processing enabled NSW’s Natural Resources
              Access Regulator (NRAR) to transform from reactive property
              inspections to proactive screening of thousands of agricultural
              properties simultaneously.
            </p>
            <div
              id="clipped-section__progress-bar"
              className="h-1 bg-gradient-to-r from-haizea-blue to-haizea-green absolute bottom-0 left-0 w-full"
              style={{
                clipPath: "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)",
              }}
            ></div>
          </div>
        </div>
      </div>
    </section>
  );
};
